import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { Users, Search, UserPlus, X, ChevronDown, Plus, Trash2, MoreVertical, Pencil, Send, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  sendTeamInvite,
  listInvitations,
  resendInvitation,
  updateInvitation,
  removeInvitation,
  checkInviteEmail,
} from "../../../services/inviteServices";
import { getMeetingRooms } from "../../../services/roomServices";


const TeamUsers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [userRooms, setUserRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [roomAssignments, setRoomAssignments] = useState([
    { roomId: "", role: "Admin" },
  ]);

  // Team data from API
  const [stats, setStats] = useState({
    totalMembers: 0,
    active: 0,
    pendingInvites: 0,
    admins: 0,
  });
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [editMember, setEditMember] = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null); // { invitationId, memberName, roomName }

  // Fetch team members and stats
  const fetchTeamData = useCallback(async () => {
    try {
      const res = await listInvitations();
      if (res?.success) {
        setStats(res.stats || { totalMembers: 0, active: 0, pendingInvites: 0, admins: 0 });
        setMembers(res.members || []);
      }
    } catch (err) {
      console.error("Failed to load team data:", err);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Filter members by search
  const filteredMembers = members.filter((m) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  });

  // Check if a room is already selected in another row
  const isRoomTaken = (roomId, excludeIndex) => {
    if (!roomId) return false;
    return roomAssignments.some(
      (a, i) => i !== excludeIndex && a.roomId === roomId
    );
  };

  const canAddMore = () => {
    // Only rooms that still have at least one open slot
    const availableRooms = userRooms.filter(
      (r) => !(r.adminTaken && r.moderatorTaken)
    );
    if (availableRooms.length <= 1) return false;
    return roomAssignments.length < availableRooms.length;
  };

  const handleAddRoom = () => {
    setRoomAssignments([...roomAssignments, { roomId: "", role: "Admin" }]);
  };

  const handleRemoveRoom = (index) => {
    setRoomAssignments(roomAssignments.filter((_, i) => i !== index));
  };

  const handleRoomChange = (index, field, value) => {
    const updated = [...roomAssignments];
    updated[index] = { ...updated[index], [field]: value };

    // When a room is picked, auto-correct the role if the default is already taken
    if (field === "roomId" && value) {
      const room = userRooms.find((r) => r.roomId === value);
      if (room) {
        const currentRole = updated[index].role;
        if (currentRole === "Admin" && room.adminTaken && !room.moderatorTaken) {
          updated[index].role = "Moderator";
        } else if (currentRole === "Moderator" && room.moderatorTaken && !room.adminTaken) {
          updated[index].role = "Admin";
        }
      }
    }

    setRoomAssignments(updated);
  };

  const hasValidAssignments = roomAssignments.some((a) => a.roomId);

  const handleInviteSubmit = async () => {
    if (!inviteEmail || !inviteName || !hasValidAssignments) return;

    const validAssignments = roomAssignments.filter((a) => a.roomId);
    setIsSending(true);

    try {
      // Pre-check the email before sending. Catches the Gmail "+suffix"
      // collision case where the email would later fail at signup, so the
      // tenant gets immediate feedback in the modal instead of waiting for
      // the invitee to click through and hit a validation error.
      try {
        const check = await checkInviteEmail(inviteEmail.trim());
        if (check && check.available === false) {
          toast.error(check.message || 'This email is treated as a duplicate of another email already in use. Please remove any "+suffix" or extra dots from the address, or enter a different email.');
          setIsSending(false);
          return;
        }
      } catch (precheckErr) {
        // If the precheck endpoint itself errors, fall through to the normal
        // send flow rather than blocking — better to attempt the invite than
        // to lock the modal on infra hiccups.
        console.warn("[TeamUsers] check-email precheck failed, proceeding:", precheckErr);
      }

      let allSuccess = true;
      for (const assignment of validAssignments) {
        const res = await sendTeamInvite({
          email: inviteEmail.trim(),
          name: inviteName.trim(),
          role: assignment.role,
          roomId: assignment.roomId,
        });
        if (!res?.success) {
          toast.error(
            res?.message || `Failed to send invitation for room`
          );
          allSuccess = false;
        }
      }
      if (allSuccess) {
        toast.success(
          validAssignments.length > 1
            ? `${validAssignments.length} invitations sent successfully`
            : "Invitation sent successfully"
        );
        setShowInviteModal(false);
        resetForm();
        fetchTeamData();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to send invitation"
      );
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setInviteEmail("");
    setInviteName("");
    setRoomAssignments([{ roomId: "", role: "Admin" }]);
  };

  const fetchUserRooms = async () => {
    setLoadingRooms(true);
    try {
      const [roomsRes, invitesRes] = await Promise.all([
        // ownedOnly=true → backend returns only rooms the current user created
        // (skips rooms where they're just a co-host). Server-side filter is
        // canonical; the previous client-side roomCreatedBy === user.id check
        // silently returned [] whenever the SiteContext user wasn't hydrated yet.
        getMeetingRooms(1, 100, true),
        listInvitations(),
      ]);

      const ownedRooms = Array.isArray(roomsRes?.data) ? roomsRes.data : [];

      // Track which room+role slots are taken by pending invitations
      const pendingInvites = (invitesRes?.invitations || []).filter(
        (inv) => inv.status === "pending"
      );

      // Annotate each room with slot availability info
      const roomsWithSlots = ownedRooms.map((room) => {
        const adminsInRoom = Array.isArray(room.roomOwnerId) ? room.roomOwnerId : [];
        const moderatorsInRoom = Array.isArray(room.coHosts) ? room.coHosts : [];

        const hasPendingAdmin = pendingInvites.some(
          (inv) => inv.roomId === room.roomId && inv.role === "Admin"
        );
        const hasPendingModerator = pendingInvites.some(
          (inv) => inv.roomId === room.roomId && inv.role === "Moderator"
        );

        return {
          ...room,
          adminTaken: adminsInRoom.length > 0 || hasPendingAdmin,
          moderatorTaken: moderatorsInRoom.length > 0 || hasPendingModerator,
        };
      });

      setUserRooms(roomsWithSlots);
    } catch (err) {
      console.error("Failed to load rooms:", err);
      setUserRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  };

  const openInviteModal = () => {
    resetForm();
    setShowInviteModal(true);
    fetchUserRooms();
  };

  const handleResendInvite = async (invitationId) => {
    setRowBusyId(invitationId);
    try {
      const res = await resendInvitation(invitationId);
      if (res?.success) {
        toast.success("Invitation resent");
        fetchTeamData();
      } else {
        toast.error(res?.message || "Failed to resend invitation");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend invitation");
    } finally {
      setRowBusyId(null);
    }
  };

  // Opens the custom confirm modal — actual removal happens in confirmRemoveAssignment
  const handleRemoveAssignment = (invitationId, context = {}) => {
    setRemoveConfirm({ invitationId, ...context });
  };

  const confirmRemoveAssignment = async () => {
    if (!removeConfirm) return;
    const { invitationId } = removeConfirm;
    setRowBusyId(invitationId);
    try {
      const res = await removeInvitation(invitationId);
      // Close modal first so toast isn't overlapped by the backdrop
      setRemoveConfirm(null);
      if (res?.success) {
        toast.success("User removed");
        await fetchTeamData();
      } else {
        toast.error(res?.message || "Failed to remove user");
      }
    } catch (err) {
      setRemoveConfirm(null);
      toast.error(err?.response?.data?.message || "Failed to remove user");
    } finally {
      setRowBusyId(null);
    }
  };

  const handleEditAssignment = async (invitationId, { name, role }) => {
    try {
      const res = await updateInvitation(invitationId, { name, role });
      if (res?.success) {
        toast.success("Updated successfully");
        fetchTeamData();
        return true;
      } else {
        toast.error(res?.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update");
    }
    return false;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6 p-3 md:p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Team Management</h2>
          <p className="text-gray-400 text-sm">
            Manage users and permissions for your organization
          </p>
        </div>
        <button
          onClick={openInviteModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-gray-400 mb-1">Total Members</p>
          <p className="text-2xl font-bold text-white">{stats.totalMembers}</p>
        </div>
        <div className="bg-white/5 rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-gray-400 mb-1">Active</p>
          <p className="text-2xl font-bold text-white">{stats.active}</p>
        </div>
        <div className="bg-white/5 rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-gray-400 mb-1">Pending Invites</p>
          <p className="text-2xl font-bold text-white">{stats.pendingInvites}</p>
        </div>
        <div className="bg-white/5 rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-gray-400 mb-1">Admins</p>
          <p className="text-2xl font-bold text-white">{stats.admins}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white/5 rounded-2xl border border-white/10">
        {/* Table Header */}
        <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr_60px] px-6 py-4 border-b border-white/10 gap-2">
          <span className="text-sm font-medium text-gray-300">User</span>
          <span className="text-sm font-medium text-gray-300">Room</span>
          <span className="text-sm font-medium text-gray-300">Role</span>
          <span className="text-sm font-medium text-gray-300">Status</span>
          <span className="text-sm font-medium text-gray-300">Date Added</span>
          <span className="text-sm font-medium text-gray-300">Last Login</span>
          <span className="text-sm font-medium text-gray-300 text-right">Actions</span>
        </div>

        {loadingMembers ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredMembers.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-5">
              <Users className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {members.length === 0 ? "No Team Members Yet" : "No matching members"}
            </h3>
            {members.length === 0 ? (
              <>
                <p className="text-gray-400 text-sm text-center max-w-sm mb-6">
                  Invite colleagues to collaborate on rooms, webinars, and live streams.
                </p>
                <button
                  onClick={openInviteModal}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Your First Team Member
                </button>
              </>
            ) : (
              <p className="text-gray-400 text-sm">Try adjusting your search</p>
            )}
          </div>
        ) : (
          /* Members List */
          <div>
            {filteredMembers.map((member, idx) => (
              <MemberRow
                key={member.email}
                member={member}
                isLast={idx === filteredMembers.length - 1}
                onEdit={() => setEditMember(member)}
                onResend={handleResendInvite}
                onRemove={handleRemoveAssignment}
                formatDate={formatDate}
                busyId={rowBusyId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className="bg-[#001233] rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8 relative border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowInviteModal(false)}
              className="absolute top-5 right-5 p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <h3 className="text-xl font-semibold text-white mb-1">
              Invite Team Member
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Send an invitation to join your organization
            </p>

            {/* Email Address */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoComplete="new-email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm"
              />
            </div>

            {/* Full Name */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name (Required)
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                autoComplete="new-name"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm"
              />
            </div>

            {/* Room & Role assignments */}
            <div className="space-y-3 mb-2">
              {roomAssignments.map((assignment, index) => (
                <div key={index} className="flex items-end gap-3">
                  {/* Room */}
                  <div className="flex-1">
                    {index === 0 && (
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Room
                      </label>
                    )}
                    <div className="relative">
                      <select
                        value={assignment.roomId}
                        onChange={(e) =>
                          handleRoomChange(index, "roomId", e.target.value)
                        }
                        disabled={loadingRooms}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm appearance-none disabled:opacity-50"
                      >
                        <option value="">
                          {loadingRooms
                            ? "Loading rooms..."
                            : userRooms.length === 0
                            ? "No rooms found"
                            : "Select a room"}
                        </option>
                        {userRooms.map((room) => {
                          const alreadyInAnotherRow = isRoomTaken(room.roomId, index);
                          const fullyAssigned = room.adminTaken && room.moderatorTaken;
                          const isDisabled = alreadyInAnotherRow || fullyAssigned;
                          return (
                            <option
                              key={room.roomId}
                              value={room.roomId}
                              disabled={isDisabled}
                            >
                              {room.roomName || room.roomId}
                              {fullyAssigned ? " (Admin & Moderator assigned)" : ""}
                            </option>
                          );
                        })}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Role */}
                  <div className="w-[140px] flex-shrink-0">
                    {index === 0 && (
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Role
                      </label>
                    )}
                    <div className="relative">
                      {(() => {
                        const selectedRoom = userRooms.find(
                          (r) => r.roomId === assignment.roomId
                        );
                        const adminTaken = selectedRoom?.adminTaken;
                        const moderatorTaken = selectedRoom?.moderatorTaken;
                        return (
                          <select
                            value={assignment.role}
                            onChange={(e) =>
                              handleRoomChange(index, "role", e.target.value)
                            }
                            disabled={!assignment.roomId}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm appearance-none disabled:opacity-50"
                          >
                            <option value="Admin" disabled={adminTaken}>
                              Admin{adminTaken ? " (taken)" : ""}
                            </option>
                            <option value="Moderator" disabled={moderatorTaken}>
                              Moderator{moderatorTaken ? " (taken)" : ""}
                            </option>
                          </select>
                        );
                      })()}
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Remove button (only show if more than one row) */}
                  {roomAssignments.length > 1 && (
                    <button
                      onClick={() => handleRemoveRoom(index)}
                      className="p-3 text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* + Add Room — shows only when all rows are filled AND more room+role slots remain */}
            {roomAssignments.every((a) => a.roomId) && canAddMore() ? (
              <div className="flex justify-end mb-6">
                <button
                  onClick={handleAddRoom}
                  className="flex items-center gap-1 text-sm text-gray-300 hover:text-white transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Room
                </button>
              </div>
            ) : (
              <div className="mb-6" />
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-6 py-2.5 border border-white/10 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteSubmit}
                disabled={
                  !inviteEmail ||
                  !inviteName ||
                  !hasValidAssignments ||
                  isSending
                }
                className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editMember && (
        <EditMemberModal
          member={editMember}
          userRooms={userRooms}
          onClose={() => setEditMember(null)}
          onSave={handleEditAssignment}
          onRemove={handleRemoveAssignment}
          onAddAssignment={async ({ email, name, role, roomId }) => {
            try {
              const res = await sendTeamInvite({ email, name, role, roomId });
              if (res?.success) {
                toast.success("Room added");
                fetchTeamData();
                fetchUserRooms();
                return true;
              }
              toast.error(res?.message || "Failed to add room");
            } catch (err) {
              toast.error(
                err?.response?.data?.message || "Failed to add room"
              );
            }
            return false;
          }}
          onOpen={fetchUserRooms}
        />
      )}

      {/* Remove Confirmation Modal */}
      {removeConfirm && (
        <ConfirmRemoveModal
          context={removeConfirm}
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={confirmRemoveAssignment}
          loading={rowBusyId === removeConfirm.invitationId}
        />
      )}
    </div>
  );
};

// ─── Confirm Remove Modal ────────────────────────────────────
function ConfirmRemoveModal({ context, onCancel, onConfirm, loading }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="bg-[#001233] rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">
              Remove Team Member?
            </h3>
            <p className="text-sm text-gray-400">
              {context.memberName ? (
                <>
                  <strong className="text-white">{context.memberName}</strong>
                  {context.roomName ? (
                    <>
                      {" "}
                      will lose <strong className="text-white">{context.role}</strong> access to{" "}
                      <strong className="text-white">{context.roomName}</strong>.
                    </>
                  ) : (
                    " will lose access to this room."
                  )}
                </>
              ) : (
                "The user will lose access to this room."
              )}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-5 py-2 border border-white/10 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Removing..." : "Yes, Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Member Row with actions menu ────────────────────────────
function MemberRow({ member, isLast, onEdit, onResend, onRemove, formatDate, busyId }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const assignments = Array.isArray(member.assignments) ? member.assignments : [];
  // Resend is allowed for any invitation that isn't yet accepted
  const resendableAssignments = assignments.filter((a) => a.status !== "accepted");

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      )
        setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [menuOpen]);

  const openMenu = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 192, // 192 = w-48
      });
    }
    setMenuOpen((v) => !v);
  };

  return (
    <div
      className={`grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr_60px] px-6 py-4 items-center gap-2 ${
        !isLast ? "border-b border-white/5" : ""
      } hover:bg-white/[0.02] transition-colors`}
    >
      {/* User */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-white truncate">{member.name}</p>
        <p className="text-xs text-gray-500 truncate">{member.email}</p>
      </div>
      {/* Room */}
      <div className="flex flex-col gap-1 min-w-0">
        {assignments.length > 0 ? (
          assignments.map((a, i) => (
            <span
              key={`${a.invitationId || i}`}
              className="text-xs text-gray-300 truncate"
              title={a.roomName || a.roomId || "—"}
            >
              {a.roomName || a.roomId || "—"}
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-500">—</span>
        )}
      </div>
      {/* Role */}
      <div className="flex flex-col gap-1">
        {assignments.length > 0 ? (
          assignments.map((a, i) => (
            <span
              key={`role-${a.invitationId || i}`}
              className={`w-fit px-2 py-0.5 rounded text-xs font-medium ${
                a.role === "Admin"
                  ? "bg-[#6a1fbf]/20 text-[#a855f7]"
                  : "bg-[#00c853]/20 text-[#00c853]"
              }`}
            >
              {a.role}
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-500">—</span>
        )}
      </div>
      {/* Status */}
      <div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            member.status === "Active"
              ? "bg-green-500/10 text-green-400"
              : "bg-yellow-500/10 text-yellow-400"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              member.status === "Active" ? "bg-green-400" : "bg-yellow-400"
            }`}
          />
          {member.status}
        </span>
      </div>
      {/* Date Added */}
      <p className="text-sm text-gray-400">{formatDate(member.dateAdded)}</p>
      {/* Last Login */}
      <p className="text-sm text-gray-400">{formatDate(member.lastLogin)}</p>
      {/* Actions menu */}
      <div className="flex justify-end">
        <button
          ref={triggerRef}
          onClick={openMenu}
          className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {menuOpen &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              style={{
                position: "absolute",
                top: menuPos.top,
                left: menuPos.left,
                zIndex: 1000,
              }}
              className="w-48 bg-[#001233] border border-white/10 rounded-lg shadow-xl py-1"
            >
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
              {resendableAssignments.map((a) => (
                <button
                  key={`resend-${a.invitationId}`}
                  onClick={() => {
                    setMenuOpen(false);
                    onResend(a.invitationId);
                  }}
                  disabled={busyId === a.invitationId}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {busyId === a.invitationId ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Resend{resendableAssignments.length > 1 ? ` (${a.roomName || a.role})` : " Invite"}
                </button>
              ))}
              {assignments.length > 0 && <div className="border-t border-white/10 my-1" />}
              {assignments.map((a) => (
                <button
                  key={`remove-${a.invitationId}`}
                  onClick={() => {
                    setMenuOpen(false);
                    onRemove(a.invitationId, {
                      memberName: member.name,
                      memberEmail: member.email,
                      roomName: a.roomName || a.roomId,
                      role: a.role,
                    });
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove{assignments.length > 1 ? ` (${a.roomName || a.role})` : ""}
                </button>
              ))}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}

// ─── Edit Member Modal ───────────────────────────────────────
function EditMemberModal({ member, userRooms = [], onClose, onSave, onRemove, onAddAssignment, onOpen }) {
  const [name, setName] = useState(member.name || "");
  const [assignments, setAssignments] = useState(() =>
    (member.assignments || []).map((a) => ({ ...a, originalRole: a.role }))
  );
  const [newAssignments, setNewAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof onOpen === "function") onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    let allOk = true;

    // Update existing assignments (name/role changes)
    for (const a of assignments) {
      const nameChanged = name !== member.name;
      const roleChanged = a.role !== a.originalRole;
      if (nameChanged || roleChanged) {
        const ok = await onSave(a.invitationId, { name, role: a.role });
        if (!ok) allOk = false;
      }
    }

    // Create new assignments
    for (const na of newAssignments) {
      if (!na.roomId) continue;
      const ok = await onAddAssignment({
        email: member.email,
        name,
        role: na.role,
        roomId: na.roomId,
      });
      if (!ok) allOk = false;
    }

    setSaving(false);
    if (allOk) onClose();
  };

  const handleRoleChange = (idx, newRole) => {
    setAssignments((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], role: newRole };
      return next;
    });
  };

  const handleNewRoomChange = (idx, field, value) => {
    setNewAssignments((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addNewRow = () => {
    setNewAssignments((prev) => [...prev, { roomId: "", role: "Admin" }]);
  };

  const removeNewRow = (idx) => {
    setNewAssignments((prev) => prev.filter((_, i) => i !== idx));
  };

  // Determine which rooms are already used (existing or just-added) so user can't pick them twice
  const usedRoomIds = new Set([
    ...assignments.map((a) => a.roomId).filter(Boolean),
    ...newAssignments.map((a) => a.roomId).filter(Boolean),
  ]);

  // Available rooms: tenant-owned rooms not already assigned to this member
  const availableRooms = userRooms.filter((r) => !usedRoomIds.has(r.roomId));

  const canAddMore = availableRooms.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#001233] rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8 relative border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-semibold text-white mb-1">Edit Team Member</h3>
        <p className="text-sm text-gray-400 mb-6">
          Update the member's name or role assignments.
        </p>

        {/* Email (read-only) */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
          <input
            type="email"
            value={member.email}
            disabled
            className="w-full px-4 py-3 bg-white/[0.03] border border-white/5 rounded-lg text-gray-500 text-sm cursor-not-allowed"
          />
        </div>

        {/* Name */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors text-sm"
          />
        </div>

        {/* Assignments */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Room Assignments
          </label>
          <div className="space-y-2">
            {assignments.length === 0 ? (
              <p className="text-sm text-gray-500">No assignments</p>
            ) : (
              assignments.map((a, idx) => (
                <div
                  key={a.invitationId}
                  className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {a.roomName || a.roomId || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {a.status === "accepted" ? "Active" : "Pending"}
                    </p>
                  </div>
                  <div className="relative w-[130px]">
                    <select
                      value={a.role}
                      onChange={(e) => handleRoleChange(idx, e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-[#6a1fbf] appearance-none"
                    >
                      <option value="Admin">Admin</option>
                      <option value="Moderator">Moderator</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </div>
                  <button
                    onClick={() =>
                      onRemove(a.invitationId, {
                        memberName: member.name,
                        memberEmail: member.email,
                        roomName: a.roomName || a.roomId,
                        role: a.role,
                      })
                    }
                    className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                    title="Remove assignment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}

            {/* New room rows being added */}
            {newAssignments.map((na, idx) => (
              <div
                key={`new-${idx}`}
                className="flex items-center gap-2 p-3 bg-[#6a1fbf]/5 border border-[#6a1fbf]/30 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <select
                      value={na.roomId}
                      onChange={(e) =>
                        handleNewRoomChange(idx, "roomId", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-[#6a1fbf] appearance-none"
                    >
                      <option value="">Select a room</option>
                      {userRooms.map((room) => {
                        const taken =
                          usedRoomIds.has(room.roomId) && room.roomId !== na.roomId;
                        return (
                          <option
                            key={room.roomId}
                            value={room.roomId}
                            disabled={taken}
                          >
                            {room.roomName || room.roomId}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="relative w-[130px]">
                  <select
                    value={na.role}
                    onChange={(e) =>
                      handleNewRoomChange(idx, "role", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-[#6a1fbf] appearance-none"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Moderator">Moderator</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                </div>
                <button
                  onClick={() => removeNewRow(idx)}
                  className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                  title="Cancel"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            {/* + Add Room button */}
            {canAddMore && (
              <button
                onClick={addNewRow}
                className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors font-medium mt-2"
              >
                <Plus className="w-4 h-4" />
                Add Room
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-white/10 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving || !name}
            className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamUsers;
